import { cn } from "@/lib/cn";

type AttributionProps = { className?: string; tone?: "light" | "dark" };

const linkClass = (tone: "light" | "dark") => cn("underline underline-offset-2", tone === "dark" ? "hover:text-white" : "hover:text-ink");
const textClass = (tone: "light" | "dark") => (tone === "dark" ? "text-white/55" : "text-ink-muted");

/** Mandatory wherever OpenStreetMap data is shown (ODbL). */
export function OsmAttribution({ className, tone = "light" }: AttributionProps) {
  return (
    <p className={cn("text-xs", textClass(tone), className)}>
      Company data ©{" "}
      <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className={linkClass(tone)}>
        OpenStreetMap contributors
      </a>
      , available under the Open Database Licence.
    </p>
  );
}

/** Wherever a Companies House register fact is shown. */
export function CompaniesHouseAttribution({ className, tone = "light" }: AttributionProps) {
  return (
    <p className={cn("text-xs", textClass(tone), className)}>
      Source:{" "}
      <a href="https://download.companieshouse.gov.uk/en_output.html" target="_blank" rel="noopener noreferrer" className={linkClass(tone)}>
        Companies House
      </a>{" "}
      (Free Company Data Product: active companies with construction SIC codes).
    </p>
  );
}

/** Directory pages list both sources together. */
export function DirectoryAttribution({ className, tone = "light" }: AttributionProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <CompaniesHouseAttribution tone={tone} />
      <OsmAttribution tone={tone} />
    </div>
  );
}

/** Registered office locations come from the ONS Postcode Directory, via postcodes.io. */
export function PostcodeAttribution({ className }: { className?: string }) {
  return (
    <p className={cn("text-[11px] leading-snug text-ink-muted", className)}>
      Location from the registered office postcode. Contains OS data © Crown copyright and database right; contains Royal Mail data
      © Royal Mail copyright and database right; source: Office for National Statistics, Open Government Licence v3.0.
    </p>
  );
}

/** ONS boundaries used for the region map (Open Government Licence v3.0). */
export function BoundaryAttribution({ className }: { className?: string }) {
  return (
    <p className={cn("text-[11px] leading-snug text-ink-muted", className)}>
      Region boundaries: Office for National Statistics, Open Government Licence v3.0. Contains OS data © Crown copyright and
      database right 2023.
    </p>
  );
}
